import { useEffect, useState } from 'react'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { useNotification } from 'hooks'
import { getEmployeesAttendance } from '../../api'
import { useMutation } from 'react-query'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { handleQueryError } from 'utils/helpers'
import { toEmployeesAllocationPayload, toEmployeesAttendancePayload } from '../serializers'
import { EmployeeAttendance } from './EmployeeAttendance'
import { useMediaQuery } from '@mui/material'
import { AllocationList } from './AllocationList'
import { AttendanceModalProps } from 'components/volumetry/interfaces'
import { validationSchema } from './helpers'

const AttendanceModal = (props: AttendanceModalProps) => {
  const {
    isModalOpen,
    closeModal,
    departmentId,
    genericDepartmentId,
    setShouldRefreshAllocationsData
  } = props
  const { showNotification } = useNotification()

  const isXs = useMediaQuery('(max-width:624px)')

  const [activeStep, setActiveStep] = useState(0)
  const [allocateEmployeesAllSelected, setAllocateEmployeesAllSelected] = useState(true)

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      employees: []
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      mutateEmployeesAttendance(toEmployeesAttendancePayload(values.employees))
      setActiveStep(1)
    }
  })

  const { isLoading: isEmployeesLoading } = getEmployeesAttendance(
    showNotification,
    isModalOpen,
    genericDepartmentId,
    values =>
      formik.setFieldValue(
        'employees',
        values.map((val, index) => ({ ...val, id: val.workerId, index }))
      )
  )

  useEffect(() => {
    const newEmployees = formik.values.employees.map(emp => ({
      ...emp,
      shouldAllocate: allocateEmployeesAllSelected
    }))
    formik.setFieldValue('employees', newEmployees)
  }, [allocateEmployeesAllSelected])

  const updateEmployeesAttendance = async values => {
    await makeCorePutRequest('/volumetry/workers-schedule', values)
  }

  const { mutate: mutateEmployeesAttendance } = useMutation(updateEmployeesAttendance, {
    onSuccess: () => {},
    onError: error =>
      handleQueryError(error, 'A aparut o eroare la alocarea automata.', showNotification)
  })

  const autoAllocateEmployees = async () => {
    await makeCorePostRequest(
      '/volumetry/tasks/automatic-allocation',
      toEmployeesAllocationPayload(formik.values.employees, departmentId)
    )
  }

  const { mutate: allocateEmployees } = useMutation(autoAllocateEmployees, {
    onSuccess: () => {
      resetModal()
      setShouldRefreshAllocationsData(true)
      showNotification({
        message: 'Alocarea automata a rulat cu succes',
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(error, 'A aparut o eroare la alocarea automata.', showNotification)
  })

  const toggleEmployee = employee => {
    const newEmployees = formik.values.employees.map(emp =>
      emp.workerId === employee.workerId ? { ...emp, shouldAllocate: !emp.shouldAllocate } : emp
    )
    formik.setFieldValue('employees', newEmployees)
  }

  const toggleAllocateEmployee = employee => {
    const newEmployees = formik.values.employees.map(emp =>
      emp.workerId === employee.workerId ? { ...emp, shouldAllocate: !emp.shouldAllocate } : emp
    )
    formik.setFieldValue('employees', newEmployees)
  }

  const updateEmployeeTime = (employee, value, field) => {
    const newEmployees = formik.values.employees.map(emp =>
      emp.workerId === employee.workerId ? { ...emp, [field]: value } : emp
    )
    formik.setFieldValue('employees', newEmployees)
  }

  const resetModal = () => {
    closeModal()
    setActiveStep(0)
    formik.resetForm()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={resetModal}
      modalTitle={
        activeStep === 0
          ? 'Confirma angajatii prezenti'
          : 'Confirma angajatii pe care vrei sa ii aloci'
      }
      modalWidth="80%"
      backAction={activeStep === 0 ? null : () => setActiveStep(0)}
    >
      {
        [
          <EmployeeAttendance
            isEmployeesLoading={isEmployeesLoading}
            employees={formik.values.employees}
            toggleEmployee={toggleEmployee}
            updateEmployeeTime={updateEmployeeTime}
            closeModal={resetModal}
            handleSubmit={formik.handleSubmit}
            isXs={isXs}
            formik={formik}
          />,
          <AllocationList
            employees={formik.values.employees.filter(employee => employee.shouldAllocate)}
            toggleEmployee={toggleAllocateEmployee}
            closeModal={resetModal}
            allocateEmployees={allocateEmployees}
            allocateEmployeesAllSelected={allocateEmployeesAllSelected}
            setAllocateEmployeesAllSelected={setAllocateEmployeesAllSelected}
          />
        ][activeStep]
      }
    </Modal>
  )
}

export default AttendanceModal
